/**
 * Shared color helpers (ported from front-v2 / ovou-ui) so every consumer
 * derives colors from one implementation: hex `#` prefixing, gradient detection,
 * perceived luminance (HSP) light/dark + close-to-white checks, with gradient
 * support.
 */

export type Shade = "light" | "dark";

export const addSharpToColorHex = (hexColor?: string): string => {
  if (!hexColor) {
    return "";
  }
  if (hexColor.includes("gradient")) {
    return hexColor;
  }
  return hexColor.includes("#") ? hexColor : `#${hexColor}`;
};

export const replaceHash = (key: string): string => key.replace("#", "");

export const isGradient = (value: string): boolean =>
  value.includes("gradient");

/**
 * Normalizes a solid hex color to the canonical 6-digit form WITHOUT `#` that
 * the backend's CardDesign contract requires. Strips a leading `#` and expands
 * 3-digit shorthand (`abc` → `aabbcc`) — react-colorful accepts shorthand but
 * the API rejects anything that isn't exactly 6 hex digits. Returns "" when the
 * input isn't a valid 3- or 6-digit hex, so callers can block the save instead
 * of letting an invalid value hit the API.
 */
export const normalizeHex = (value: string): string => {
  const hex = value.replace("#", "").trim();
  if (/^[a-f\d]{6}$/i.test(hex)) {
    return hex;
  }
  if (/^[a-f\d]{3}$/i.test(hex)) {
    return hex
      .split("")
      .map(c => c + c)
      .join("");
  }
  return "";
};

/**
 * Pulls the first solid hex color out of a gradient string, returned as a
 * 6-hex WITHOUT `#` (the form the solid picker seeds from). Expands 3-digit
 * shorthand; returns "" when the gradient carries no hex stop. Used so that
 * reopening the Primary color modal on a gradient seeds the solid picker from
 * the gradient's own first color instead of falling back to black.
 */
export const firstHexFromGradient = (gradient: string): string => {
  const match = gradient.match(/#([a-f\d]{6}|[a-f\d]{3})\b/i);
  return match ? normalizeHex(match[1]).toLowerCase() : "";
};

export const isValidGradient = (value: string): boolean =>
  /^linear-gradient\(.+\)$/.test(value.trim());

const HSP_LIGHT_THRESHOLD = 165;
const HSP_WHITE_THRESHOLD = 240;

const COLOR_IN_STRING_REGEX =
  /#(?:[a-f\d]{3}){1,2}\b|rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*[\d.]+)?\s*\)|hsla?\(\s*[\d.]+\s*,\s*[\d.]+%\s*,\s*[\d.]+%(?:\s*,\s*[\d.]+)?\s*\)/gi;

function parseHslToRgb(h: number, s: number, l: number) {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number): number => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
  };
  return {
    r: Math.round(f(0) * 255),
    g: Math.round(f(8) * 255),
    b: Math.round(f(4) * 255),
  };
}

function calculateHSP(color: string): number | undefined {
  let r: number | undefined,
    g: number | undefined,
    b: number | undefined,
    match;
  if (color.match(/^rgb/)) {
    match = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)$/);
    if (match) {
      r = Number(match[1]);
      g = Number(match[2]);
      b = Number(match[3]);
    }
  } else if (color.match(/^hsl/)) {
    match = color.match(
      /^hsla?\(\s*([\d.]+),\s*([\d.]+)%,\s*([\d.]+)%(?:,\s*[\d.]+)?\)$/,
    );
    if (match) {
      const rgb = parseHslToRgb(
        Number(match[1]),
        Number(match[2]),
        Number(match[3]),
      );
      r = rgb.r;
      g = rgb.g;
      b = rgb.b;
    }
  } else {
    match = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(color);
    if (match) {
      r = parseInt(match[1] + match[1], 16);
      g = parseInt(match[2] + match[2], 16);
      b = parseInt(match[3] + match[3], 16);
    } else {
      match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
      if (match) {
        r = parseInt(match[1], 16);
        g = parseInt(match[2], 16);
        b = parseInt(match[3], 16);
      }
    }
  }
  if (r !== undefined && g !== undefined && b !== undefined) {
    return Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
  }
  return undefined;
}

function calculateHSPForGradient(gradient: string): number | undefined {
  const matches: string[] = gradient.match(COLOR_IN_STRING_REGEX) ?? [];
  const colors = matches.filter((c, i) => matches.indexOf(c) === i);
  if (colors.length === 0) {
    return undefined;
  }
  const values: number[] = [];
  for (const color of colors) {
    const normalized =
      color.startsWith("rgb") ||
      color.startsWith("hsl") ||
      color.startsWith("#")
        ? color
        : `#${color}`;
    const hsp = calculateHSP(normalized);
    if (hsp !== undefined) {
      values.push(hsp);
    }
  }
  if (values.length === 0) {
    return undefined;
  }
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function getHSP(color: string): number | undefined {
  const trimmed = color.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed.includes("gradient")
    ? calculateHSPForGradient(color)
    : calculateHSP(color);
}

export function lightOrDark(color: string): Shade | "" {
  const hsp = getHSP(color);
  if (hsp === undefined) {
    return "";
  }
  return hsp > HSP_LIGHT_THRESHOLD ? "light" : "dark";
}

export function isCloseToWhite(color: string): boolean {
  const hsp = getHSP(color);
  if (hsp === undefined) {
    return false;
  }
  return hsp > HSP_WHITE_THRESHOLD;
}
